import { PDF_PAGE_DIMENSIONS, CHART_MAX_POINTS, PDF_BRAND_BLUE } from "./pdfExportConstants";
import { PdfReportData, FaceAnalysis, SubsystemAnalysis } from "./pdfExportTypes";
import { downsampleSeries, downsampleTimeSeries, formatTime, formatDuration } from "../../hooks/pdfExportHooks";
import type { ScheduleTaskType } from "../../../../../utils/scenario-types";
import type { EnrichedAlert } from "./pdfExportTypes";
import { convertDecimalMinutesToMMSS } from "../../../../../../../../utils/commonFunctions";

interface PageWrapperOptions {
  title: string;
  pageNumber: number;
  totalPages: number;
  contentHtml: string;
  satelliteName?: string;
  startTimeFormatted?: string;
  endTimeFormatted?: string;
}

/**
 * Wraps page content HTML with the standard outer shell: header with logo/satellite info,
 * centered content grid, and footer with generation date and page number.
 */
function wrapPageHtml({
  title,
  pageNumber,
  totalPages,
  contentHtml,
  satelliteName = "—",
  startTimeFormatted = "—",
  endTimeFormatted = "—",
}: PageWrapperOptions): string {
  const generationDate = new Date().toLocaleDateString();

  // Inline Antaris logo SVG (62x18 viewBox, scaled to ~90px width maintaining aspect)
  const logoSvg = `<svg width="90" height="26" viewBox="0 0 62 18" fill="none" xmlns="http://www.w3.org/2000/svg" style="flex-shrink: 0;">
<path d="M9.62069 8.94027C14.7874 4.66441 22.3889 3.47667 25.477 3.41728C25.477 3.41728 25.8333 3.41728 25.8333 3.7736C25.8333 4.12992 25.477 4.12992 25.477 4.12992C22.3889 4.18931 13.2804 5.91153 9.62069 8.94027Z" fill="#1F2638"/>
<path d="M51.8448 8.94027C46.6782 4.66441 39.0766 3.47667 35.9885 3.41728C35.9885 3.41728 35.6322 3.41728 35.6322 3.7736C35.6322 4.12992 35.9885 4.12992 35.9885 4.12992C39.0766 4.18931 48.1851 5.91153 51.8448 8.94027Z" fill="#1F2638"/>
<path d="M30.9199 2.3107C30.8812 2.27194 30.8286 2.25017 30.7738 2.25017H30.3855C30.3307 2.25017 30.2781 2.27194 30.2393 2.3107L28.9372 3.61286C28.8984 3.65161 28.8766 3.70418 28.8766 3.75899V4.14734C28.8766 4.20215 28.8984 4.25472 28.9372 4.29348L29.6676 5.02391C29.7064 5.06267 29.7589 5.08444 29.8137 5.08444H30.2021C30.2569 5.08444 30.3095 5.06267 30.3482 5.02391L31.6504 3.72175C31.6891 3.683 31.7109 3.63043 31.7109 3.57562V3.18727C31.7109 3.13246 31.6891 3.07989 31.6504 3.04114L30.9199 2.3107Z" fill="#1F2638"/>
<path d="M28.8642 4.36645C28.8254 4.32769 28.7729 4.30591 28.718 4.30591H28.6581C28.6033 4.30591 28.5508 4.32769 28.512 4.36645L28.4019 4.4765C28.3632 4.51526 28.3414 4.56783 28.3414 4.62264V4.68255C28.3414 4.73736 28.3632 4.78993 28.4019 4.82869L29.1324 5.55912C29.1711 5.59788 29.2237 5.61966 29.2785 5.61966H29.3384C29.3932 5.61966 29.4458 5.59788 29.4846 5.55912L29.5946 5.44907C29.6334 5.41031 29.6551 5.35774 29.6551 5.30293V5.24302C29.6551 5.18821 29.6334 5.13564 29.5946 5.09688L28.8642 4.36645Z" fill="#1F2638"/>
<path d="M30.9796 1.0849L30.7803 1.61121C30.7396 1.71877 30.7657 1.84021 30.8471 1.92153L32.0396 3.11403C32.1209 3.19535 32.2423 3.22146 32.3499 3.18074L32.8762 2.98152C33.0514 2.9152 33.0998 2.69018 32.9673 2.55772L32.4773 2.0677V1.56983C32.4773 1.52232 32.4388 1.48381 32.3913 1.48381H31.8934L31.4034 0.993791C31.2709 0.861331 31.0459 0.909707 30.9796 1.0849Z" fill="#1F2638"/>
<path d="M29.6673 2.61497L28.5361 1.48369L27.9045 2.11529C27.8033 2.2164 27.8332 2.3872 27.9626 2.44806L29.1042 2.98506C29.183 3.0221 29.2765 3.00577 29.3381 2.94423L29.6673 2.61497Z" fill="#1F2638"/>
<path d="M29.716 2.56634L28.5847 1.43506L29.2163 0.803459C29.3174 0.702347 29.4882 0.732171 29.5491 0.861566L30.0861 2.00324C30.1231 2.082 30.1068 2.17553 30.0453 2.23708L29.9654 2.31697L29.5518 1.22219L29.5214 1.2526L29.9319 2.35042L29.716 2.56634Z" fill="#1F2638"/>
<path d="M29.1929 0.36457L28.8888 0.0604633C28.8082 -0.0201544 28.6775 -0.0201544 28.5969 0.0604633L27.9035 0.753827L28.4996 1.34988L28.9403 0.909142L28.831 0.616979L28.8615 0.586568L28.9715 0.877922L29.1929 0.656513C29.2735 0.575895 29.2735 0.445188 29.1929 0.36457Z" fill="#1F2638"/>
<path d="M27.8549 0.802536L28.4509 1.39859L27.7575 2.09195C27.6769 2.17257 27.5462 2.17257 27.4656 2.09195L27.1615 1.78784C27.0809 1.70722 27.0809 1.57652 27.1615 1.4959L27.8549 0.802536Z" fill="#1F2638"/>
<path d="M31.3461 4.29387L32.4773 5.42515L31.8457 6.05675C31.7446 6.15786 31.5738 6.12804 31.513 5.99864L30.976 4.85697C30.9389 4.77821 30.9553 4.68467 31.0168 4.62313L31.3461 4.29387Z" fill="#1F2638"/>
<path d="M31.3947 4.24524L32.526 5.37652L33.1576 4.74492C33.2587 4.64381 33.2289 4.47301 33.0995 4.41214L31.9578 3.87515C31.879 3.83811 31.7855 3.85444 31.724 3.91598L31.6441 3.99587L32.7389 4.40946L32.7084 4.43987L31.6106 4.02933L31.3947 4.24524Z" fill="#1F2638"/>
<path d="M33.5964 4.76826L33.9006 5.07237C33.9812 5.15298 33.9812 5.28369 33.9006 5.36431L33.2072 6.05767L32.6111 5.46162L33.0519 5.02089L33.344 5.13015L33.3744 5.09974L33.0831 4.98967L33.3045 4.76826C33.3851 4.68764 33.5158 4.68764 33.5964 4.76826Z" fill="#1F2638"/>
<path d="M33.1585 6.10634L32.5625 5.51029L31.8691 6.20366C31.7885 6.28428 31.7885 6.41498 31.8691 6.4956L32.1732 6.79971C32.2538 6.88033 32.3845 6.88033 32.4652 6.79971L33.1585 6.10634Z" fill="#1F2638"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M0 17.6702H2.13793L3.2069 15.5322H7.83908L8.90805 17.6702H11.046L6.77011 10.3656H4.27586L0 17.6702ZM4.27586 13.9288L5.52299 11.969L6.77011 13.9288H4.27586Z" fill="#1F2638"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M28.1494 17.6702H30.2874L31.3563 15.5322H35.9885L37.0575 17.6702H39.1954L34.9195 10.3656H32.4253L28.1494 17.6702ZM32.4253 13.9288L33.6724 11.969L34.9195 13.9288H32.4253Z" fill="#1F2638"/>
<path d="M11.4023 17.6702H13.5402V12.5035L18.8851 15.1759V17.6702H21.023V10.3656H18.8851V13.3943L13.5402 10.3656H11.4023V17.6702Z" fill="#1F2638"/>
<path d="M24.9425 17.6702V12.1472H21.7356V10.3656H30.2874V12.1472H27.0805V17.6702H24.9425Z" fill="#1F2638"/>
<path d="M39.5517 17.6702V13.9288H46.6782C46.875 13.9288 47.0345 13.7692 47.0345 13.5725V12.5035C47.0345 12.3067 46.875 12.1472 46.6782 12.1472H39.5517V10.3656H47.0345C48.6023 10.3656 48.9943 11.4345 48.9943 11.969V14.1069C48.9943 15.1046 48.2816 15.5322 47.0345 15.5322L48.2816 17.6702H46.1437L44.8966 15.5322H41.6897V17.6702H39.5517Z" fill="#1F2638"/>
<path d="M49.5287 17.6702H51.6667V10.3656H49.5287V17.6702Z" fill="#1F2638"/>
<path d="M59.8621 17.6702H52.3793V15.8885H59.3276C59.6228 15.8885 59.8621 15.6492 59.8621 15.3541C59.8621 15.0589 59.6228 14.8196 59.3276 14.8196H54.1609C52.7356 14.8196 52.3793 13.3943 52.3793 12.5035C52.3793 11.6127 53.092 10.3656 54.5172 10.3656H60.931V11.969L54.6954 11.969C54.4002 11.969 54.1609 12.2083 54.1609 12.5035C54.1609 12.7987 54.4002 13.038 54.6954 13.038H59.8621C61.4655 13.038 62 14.6414 62 15.3541C62 16.1414 61.4655 17.6702 59.8621 17.6702Z" fill="#1F2638"/>
</svg>`;

  return `
    <div style="width: ${PDF_PAGE_DIMENSIONS.width}px; height: ${PDF_PAGE_DIMENSIONS.height}px; padding: ${PDF_PAGE_DIMENSIONS.paddingPx}px; box-sizing: border-box; background: white; display: grid; grid-template-rows: auto auto 1fr auto; font-family: ${PDF_PAGE_DIMENSIONS.fontFamily}; color: #000; line-height: 1.5;">
      <header style="display: grid; grid-template-columns: auto 1fr; align-items: start; gap: 16px; padding-bottom: 12px; margin-bottom: 16px; border-bottom: 1px solid #e5e5e5;">
        <div style="display: grid; grid-template-columns: 1fr; grid-auto-rows: auto; row-gap: 2px;">
          <div style="display: flex; align-items: center;">${logoSvg}</div>
          <div style="font-size: 12px; font-weight: 600; color: #333; margin-top: 2px;">${satelliteName}</div>
        </div>
        <div style="display: grid; grid-template-columns: 1fr; grid-auto-rows: auto; row-gap: 2px; justify-items: end;">
          <div style="font-size: 12px; color: #444;">Start: ${startTimeFormatted}</div>
          <div style="font-size: 12px; color: #444;">End: ${endTimeFormatted}</div>
        </div>
      </header>
      <div style="margin-bottom: ${title ? "16px" : "0"};">${title ? `<h1 style="font-size: 24px; font-weight: 700; color: ${PDF_BRAND_BLUE}; margin: 0;">${title}</h1>` : ""}</div>
      <div style="overflow: hidden;">
        ${contentHtml}
      </div>
      <footer style="padding-top: 16px; border-top: 1px solid #e5e5e5; display: grid; grid-template-columns: 1fr auto; align-items: center; gap: 16px; font-size: 9px; color: #666;">
        <div>${generationDate}</div>
        <div>Page ${pageNumber} of ${totalPages}</div>
      </footer>
    </div>
  `;
}

export function buildCoverPageHtml(
  metadata: PdfReportData["metadata"],
  summary: PdfReportData["summary"],
  environment: PdfReportData["environment"],
  pageNumber: number,
  totalPages: number,
  satelliteName: string = "—",
  startTimeFormatted: string = "—",
  endTimeFormatted: string = "—"
): string {
  const duration = formatDuration(metadata.durationSec);

  // Helper to format orbital parameters
  const formatOrbitalParam = (value: number, unit: string = "") => {
    if (typeof value !== "number" || Number.isNaN(value)) return "—";
    const formatted = Math.abs(value) < 0.01 ? value.toExponential(2) : value.toFixed(2);
    return unit ? `${formatted} ${unit}` : formatted;
  };

  // Helper to get alert status color based on critical alert count
  const getAlertColor = (count: number) => {
    if (count === 0) return { border: "#4caf50", bg: "#e8f5e9" };
    if (count <= 4) return { border: "#ff9800", bg: "#fff3e0" };
    return { border: "#d32f2f", bg: "#ffebee" };
  };

  const alertColor = getAlertColor(summary.criticalAlertCount);

  // Helper to get thermal compliance status banner (3-tier: PASS, MARGINAL, FAIL)
  // Considers both critical alerts (1-2 = marginal, 3+ = fail) and warning alerts (6+ with 0 criticals = marginal)
  const getComplianceStatus = (criticalAlertCount: number, warningAlertCount: number) => {
    // FAIL: 3 or more critical alerts
    if (criticalAlertCount >= 3) {
      return {
        label: "FAILED THERMAL COMPLIANCE",
        description: "One or more faces or subsystems exceeded defined thermal thresholds during the mission timeline.",
        border: "#d32f2f",
        bg: "#ffebee",
        textColor: "#c62828",
        icon: `<svg width="40" height="40" viewBox="0 0 40 40" style="flex-shrink: 0;">
          <circle cx="20" cy="20" r="18" fill="#d32f2f"/>
          <path d="M 12 12 L 28 28 M 28 12 L 12 28" stroke="white" stroke-width="2.5" stroke-linecap="round"/>
        </svg>`,
      };
    }
    // MARGINAL: 1-2 critical alerts (critical-triggered)
    if (criticalAlertCount >= 1 && criticalAlertCount <= 2) {
      return {
        label: "MARGINAL COMPLIANCE",
        description: "One or two critical thermal thresholds were exceeded during the mission timeline; further review is recommended.",
        border: "#ff9800",
        bg: "#fff3e0",
        textColor: "#e65100",
        icon: `<svg width="40" height="40" viewBox="0 0 40 40" style="flex-shrink: 0;">
          <circle cx="20" cy="20" r="18" fill="#ff9800"/>
          <text x="20" y="26" font-size="24" font-weight="bold" fill="white" text-anchor="middle">!</text>
        </svg>`,
      };
    }
    // MARGINAL: 0 critical alerts but 6+ warning alerts (warning-triggered)
    if (criticalAlertCount === 0 && warningAlertCount >= 6) {
      return {
        label: "MARGINAL COMPLIANCE",
        description: "A high number of thermal warning alerts were detected during the mission timeline; further review is recommended.",
        border: "#ff9800",
        bg: "#fff3e0",
        textColor: "#e65100",
        icon: `<svg width="40" height="40" viewBox="0 0 40 40" style="flex-shrink: 0;">
          <circle cx="20" cy="20" r="18" fill="#ff9800"/>
          <text x="20" y="26" font-size="24" font-weight="bold" fill="white" text-anchor="middle">!</text>
        </svg>`,
      };
    }
    // PASS: 0 critical alerts and 5 or fewer warning alerts
    return {
      label: "PASSED THERMAL COMPLIANCE",
      description: "All faces and subsystems remained within defined thermal thresholds throughout the mission timeline.",
      border: "#4caf50",
      bg: "#e8f5e9",
      textColor: "#2e7d32",
      icon: `<svg width="40" height="40" viewBox="0 0 40 40" style="flex-shrink: 0;">
        <circle cx="20" cy="20" r="18" fill="#4caf50"/>
        <path d="M 12 20 L 17 25 L 28 14" stroke="white" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`,
    };
  };

  // Calculate warning alert count: total violations minus critical alerts
  const warningAlertCount = summary.violationCount - summary.criticalAlertCount;
  const compliance = getComplianceStatus(summary.criticalAlertCount, warningAlertCount);

  const contentHtml = `
    <div style="display: grid; grid-template-columns: 1fr; grid-auto-rows: auto; row-gap: 16px;">
      <!-- Title/Branding Box -->
      <div style="border-left: 5px solid ${PDF_BRAND_BLUE}; background: #f5f9fc; padding: 12px 16px; display: grid; grid-template-columns: 1fr; grid-auto-rows: auto; row-gap: 4px;">
        <div style="font-size: 11px; font-weight: 600; color: ${PDF_BRAND_BLUE}; text-transform: uppercase; letter-spacing: 0.5px;">Antaris Space Platform</div>
        <div style="font-size: 18px; font-weight: 700; color: #000;">${metadata.scenarioName} Scenario Thermal Analysis</div>
        <div style="font-size: 14px; color: #666; line-height: 1.4;">Per-second thermal analysis of spacecraft surface and internal deck temperatures across the mission timeline.</div>
      </div>

      <!-- Thermal Compliance Status Banner -->
      <div style="display: grid; grid-template-columns: auto 1fr; align-items: center; column-gap: 12px; border: 1px solid ${compliance.border}; border-radius: 6px; background: ${compliance.bg}; padding: 12px 16px;">
        ${compliance.icon}
        <div style="display: grid; grid-template-columns: 1fr; row-gap: 2px;">
          <div style="font-size: 13px; font-weight: 700; color: ${compliance.textColor}; text-transform: uppercase; letter-spacing: 0.3px;">${compliance.label}</div>
          <div style="font-size: 12px; color: #444;">${compliance.description}</div>
        </div>
      </div>

      <!-- Mission Summary Stat Tiles -->
      <div style="display: grid; grid-template-columns: repeat(4, 1fr); column-gap: 12px; row-gap: 12px;">
        <div style="border-top: 4px solid ${PDF_BRAND_BLUE}; border-radius: 2px; padding: 10px; background: #f9f9f9; text-align: center; display: grid; grid-template-columns: 1fr; grid-auto-rows: auto; row-gap: 4px;">
          <div style="font-size: 12px; font-weight: 600; color: #333; text-transform: uppercase; letter-spacing: 0.5px;">Max Temperature</div>
          <div style="font-size: 16px; font-weight: 700; color: #000;">${summary.maxTemp.toFixed(1)}°C</div>
        </div>
        <div style="border-top: 4px solid ${PDF_BRAND_BLUE}; border-radius: 2px; padding: 10px; background: #f9f9f9; text-align: center; display: grid; grid-template-columns: 1fr; grid-auto-rows: auto; row-gap: 4px;">
          <div style="font-size: 12px; font-weight: 600; color: #333; text-transform: uppercase; letter-spacing: 0.5px;">Min Temperature</div>
          <div style="font-size: 16px; font-weight: 700; color: #000;">${summary.minTemp.toFixed(1)}°C</div>
        </div>
        <div style="border-top: 4px solid ${PDF_BRAND_BLUE}; border-radius: 2px; padding: 10px; background: #f9f9f9; text-align: center; display: grid; grid-template-columns: 1fr; grid-auto-rows: auto; row-gap: 4px;">
          <div style="font-size: 12px; font-weight: 600; color: #333; text-transform: uppercase; letter-spacing: 0.5px;">Avg Temperature</div>
          <div style="font-size: 16px; font-weight: 700; color: #000;">${summary.avgTemp.toFixed(1)}°C</div>
        </div>
        <div style="border-top: 4px solid ${alertColor.border}; border-radius: 2px; padding: 10px; background: ${alertColor.bg}; text-align: center; display: grid; grid-template-columns: 1fr; grid-auto-rows: auto; row-gap: 4px;">
          <div style="font-size: 12px; font-weight: 600; color: #333; text-transform: uppercase; letter-spacing: 0.5px;">Critical Alerts</div>
          <div style="font-size: 16px; font-weight: 700; color: ${alertColor.border};">${summary.criticalAlertCount}</div>
        </div>
      </div>

      <!-- Two-Column Details -->
      <div style="display: grid; grid-template-columns: 1fr 1fr; column-gap: 16px;">
        <!-- Scenario Details -->
        <div style="border: 1px solid #ddd; border-radius: 4px; padding: 12px;">
          <div style="font-size: 16px; font-weight: 500; color: #000; text-transform: uppercase; margin-bottom: 10px; padding-bottom: 8px; border-bottom: 1px solid #eee;">Scenario Details</div>
          <div style="display: grid; grid-template-columns: auto 1fr; column-gap: 12px; row-gap: 6px; font-size: 14px;">
            <div style="color: #333; font-weight: 500;">Scenario:</div>
            <div style="color: #000;">${metadata.scenarioName}</div>
            <div style="color: #333; font-weight: 500;">Satellite:</div>
            <div style="color: #000;">${metadata.satelliteName}</div>
            <div style="color: #333; font-weight: 500;">Analysis Date:</div>
            <div style="color: #000;">${metadata.analysisDate}</div>
            <div style="color: #333; font-weight: 500;">Duration:</div>
            <div style="color: #000;">${duration}</div>
            <div style="color: #333; font-weight: 500;">Data Resolution:</div>
            <div style="color: #000;">${metadata.dataResolutionSec} sec</div>
            <!-- Orbits Covered field hidden for now — data validation needed before re-enabling
            <div style="color: #333; font-weight: 500;">Orbits Covered:</div>
            <div style="color: #000;">${metadata.orbitsCovered}</div>
            -->
            <div style="color: #333; font-weight: 500;">Decks Analyzed:</div>
            <div style="color: #000;">${metadata.decksAnalyzed}</div>
          </div>
        </div>

        <!-- Orbital Parameters -->
        <div style="border: 1px solid #ddd; border-radius: 4px; padding: 12px;">
          <div style="font-size: 16px; font-weight: 500; color: #000; text-transform: uppercase; margin-bottom: 10px; padding-bottom: 8px; border-bottom: 1px solid #eee;">Orbital Parameters</div>
          <div style="display: grid; grid-template-columns: auto 1fr; column-gap: 12px; row-gap: 6px; font-size: 14px;">
            <div style="color: #333; font-weight: 500;">Altitude:</div>
            <div style="color: #000;">${formatOrbitalParam(metadata.orbitalParameters.altitude, "km")}</div>
            <div style="color: #333; font-weight: 500;">Eccentricity:</div>
            <div style="color: #000;">${formatOrbitalParam(metadata.orbitalParameters.eccentricity)}</div>
            <div style="color: #333; font-weight: 500;">Inclination:</div>
            <div style="color: #000;">${formatOrbitalParam(metadata.orbitalParameters.inclination, "°")}</div>
            <div style="color: #333; font-weight: 500;">RAAN:</div>
            <div style="color: #000;">${formatOrbitalParam(metadata.orbitalParameters.RAAN, "°")}</div>
            <div style="color: #333; font-weight: 500;">Arg. of Perigee:</div>
            <div style="color: #000;">${formatOrbitalParam(metadata.orbitalParameters.AP, "°")}</div>
            <div style="color: #333; font-weight: 500;">True Anomaly:</div>
            <div style="color: #000;">${formatOrbitalParam(metadata.orbitalParameters.TA, "°")}</div>
          </div>
        </div>
      </div>

      <!-- Environmental Parameters -->
      <div style="display: grid; grid-template-columns: repeat(3, 1fr); column-gap: 12px;">
        <div style="border-top: 4px solid ${PDF_BRAND_BLUE}; border-radius: 2px; padding: 10px; background: #f9f9f9; text-align: center; display: grid; grid-template-columns: 1fr; grid-auto-rows: auto; row-gap: 4px;">
          <div style="font-size: 12px; font-weight: 600; color: #333; text-transform: uppercase; letter-spacing: 0.5px;">Solar Constant</div>
          <div style="font-size: 16px; font-weight: 700; color: #000;">${environment.solarConstant.toFixed(1)} W/m²</div>
        </div>
        <div style="border-top: 4px solid ${PDF_BRAND_BLUE}; border-radius: 2px; padding: 10px; background: #f9f9f9; text-align: center; display: grid; grid-template-columns: 1fr; grid-auto-rows: auto; row-gap: 4px;">
          <div style="font-size: 12px; font-weight: 600; color: #333; text-transform: uppercase; letter-spacing: 0.5px;">Albedo Factor</div>
          <div style="font-size: 16px; font-weight: 700; color: #000;">${(environment.albedoFactor * 100).toFixed(1)}%</div>
        </div>
        <div style="border-top: 4px solid ${PDF_BRAND_BLUE}; border-radius: 2px; padding: 10px; background: #f9f9f9; text-align: center; display: grid; grid-template-columns: 1fr; grid-auto-rows: auto; row-gap: 4px;">
          <div style="font-size: 12px; font-weight: 600; color: #333; text-transform: uppercase; letter-spacing: 0.5px;">Blackbody Temp</div>
          <div style="font-size: 16px; font-weight: 700; color: #000;">${environment.blackbodyTemp.toFixed(1)} K</div>
        </div>
      </div>
    </div>
  `;

  return wrapPageHtml({
    title: "",
    pageNumber,
    totalPages,
    contentHtml,
    satelliteName,
    startTimeFormatted,
    endTimeFormatted,
  });
}

export function buildMissionProfileHtml(
  chartImageDataUrl: string,
  pageNumber: number,
  totalPages: number,
  figureNumber: number = 1,
  satelliteName: string = "—",
  startTimeFormatted: string = "—",
  endTimeFormatted: string = "—"
): string {
  const contentHtml = `
    <div style="display: grid; grid-template-columns: 1fr; grid-auto-rows: auto; row-gap: 16px;">
      <div style="font-size: 14px; color: #000; line-height: 1.6;">
        <p style="margin: 0 0 12px 0;">
          The thermal profile chart below shows the spacecraft's global minimum, maximum, and average temperatures across the entire mission timeline.
          This provides a high-level view of thermal behavior and identifies periods of elevated or reduced thermal stress.
        </p>
      </div>
      <div style="display: grid; grid-template-columns: 1fr; grid-auto-rows: auto; row-gap: 8px;">
        <img src="${chartImageDataUrl}" style="max-width: 100%; height: auto;" alt="Thermal Profile Chart" />
        <div style="font-size: 14px; color: #000; text-align: center;">Fig. ${figureNumber}: Overall Scenario Thermal Profile: Temperature Vs Time</div>
      </div>
    </div>
  `;

  return wrapPageHtml({
    title: "Mission Thermal Profile",
    pageNumber,
    totalPages,
    contentHtml,
    satelliteName,
    startTimeFormatted,
    endTimeFormatted,
  });
}

/**
 * Build a reusable SVG line chart that handles arbitrary series data.
 * Supports multiple series with auto-scaled axes, legend below chart, and axis titles.
 */
export function buildLineChartSvg(
  series: Array<{ label: string; color: string; points: Array<{ x: number; y: number }> }>,
  options: { width?: number; height?: number; yLabel?: string; showLegend?: boolean; xTickMode?: "all" | "sparse"; showLineLabels?: boolean } = {}
): string {
  const baseWidth = options.width ?? 700;
  const inputHeight = options.height ?? 300;
  const showLegend = options.showLegend !== false;
  const xTickMode = options.xTickMode ?? "all";
  const showLineLabels = options.showLineLabels ?? false;

  if (series.length === 0 || series.every(s => s.points.length === 0)) {
    return `<svg width="${baseWidth}" height="${inputHeight}" style="background: white; font-family: sans-serif;"><text x="10" y="20" style="font-size: 12px; fill: #999;">No data</text></svg>`;
  }

  // Padding for margins
  const paddingLeft = 60;
  const paddingRight = 20;
  const paddingTop = 20;
  const paddingBottomChart = 40;
  const legendRowHeight = 20;
  const legendRowsNeeded = showLegend ? Math.ceil(series.length / 4) : 0;
  const legendPaddingBottom = legendRowsNeeded * legendRowHeight + 20;

  // Chart area height is input minus space for legend/labels
  const xAxisTitleSpace = 20;
  const baseHeight = inputHeight - legendPaddingBottom - xAxisTitleSpace;
  const chartHeight = baseHeight - paddingTop - paddingBottomChart;
  const chartWidth = baseWidth - paddingLeft - paddingRight;

  const width = baseWidth;
  const height = inputHeight;

  // Find global min/max for Y-axis
  let globalMin = Infinity, globalMax = -Infinity, minX = Infinity, maxX = -Infinity;
  for (const s of series) {
    for (const point of s.points) {
      if (point.y < globalMin) globalMin = point.y;
      if (point.y > globalMax) globalMax = point.y;
      if (point.x < minX) minX = point.x;
      if (point.x > maxX) maxX = point.x;
    }
  }
  const rangeY = globalMax - globalMin || 100;
  const rangeX = maxX - minX || 1;

  // Build polylines for each series
  const polylines = series.map((s) => {
    const points = s.points
      .map((p) => {
        const x = paddingLeft + ((p.x - minX) / rangeX) * chartWidth;
        const y = paddingTop + chartHeight - ((p.y - globalMin) / rangeY) * chartHeight;
        return `${x},${y}`;
      })
      .join(" ");
    return `<polyline points="${points}" style="stroke: ${s.color}; stroke-width: 2; fill: none;"/>`;
  }).join("\n");

  // Grid lines (drawn first so axes paint on top)
  // Horizontal grid lines for Y-axis
  const horizontalGridLines = [0, 1, 2, 3, 4, 5]
    .map(
      (i) => {
        const y = paddingTop + (i * chartHeight) / 5;
        return `<line x1="${paddingLeft}" y1="${y}" x2="${width - paddingRight}" y2="${y}" stroke="#ddd" stroke-width="1"/>`;
      }
    )
    .join("\n");

  // Vertical grid lines for X-axis
  const verticalGridLines = [0, 1, 2, 3, 4, 5]
    .map(
      (i) => {
        const x = paddingLeft + (i * chartWidth) / 5;
        return `<line x1="${x}" y1="${paddingTop}" x2="${x}" y2="${paddingTop + chartHeight}" stroke="#ddd" stroke-width="1"/>`;
      }
    )
    .join("\n");

  const gridLines = horizontalGridLines + "\n" + verticalGridLines;

  // Y-axis tick labels
  const yTickLabels = [0, 1, 2, 3, 4, 5]
    .map(
      (i) => {
        const y = paddingTop + chartHeight - (i * chartHeight) / 5;
        const temp = globalMin + (rangeY * i) / 5;
        return `<text x="${paddingLeft - 10}" y="${y + 4}" text-anchor="end" class="chart-label">${temp.toFixed(0)}°C</text>`;
      }
    )
    .join("\n");

  // X-axis tick labels (real time values)
  const xTickIndices = xTickMode === "sparse" ? [0, 3, 5] : [0, 1, 2, 3, 4, 5];
  const xTickLabels = xTickIndices
    .map(
      (i) => {
        const x = paddingLeft + (i * chartWidth) / 5;
        const timeVal = minX + (rangeX * i) / 5;
        const timeStr = formatTime(timeVal);
        return `<text x="${x}" y="${paddingTop + chartHeight + 20}" text-anchor="middle" class="chart-label">${timeStr}</text>`;
      }
    )
    .join("\n");

  // Axis titles
  const yAxisTitle = `<text x="15" y="${paddingTop + chartHeight / 2}" text-anchor="middle" transform="rotate(-90 15 ${paddingTop + chartHeight / 2})" style="font-size: 12px; fill: #333; font-weight: 500;">Temperature (°C)</text>`;
  const xAxisTitleY = paddingTop + chartHeight + 35;
  const xAxisTitle = `<text x="${paddingLeft + chartWidth / 2}" y="${xAxisTitleY}" text-anchor="middle" style="font-size: 12px; fill: #333; font-weight: 500;">Time (hh:mm:ss)</text>`;

  // Legend below X-axis title (if enabled)
  const legend = showLegend
    ? series
        .map((s, index) => {
          const row = Math.floor(index / 4);
          const col = index % 4;
          const xPos = paddingLeft + col * (chartWidth / 4) + 10;
          const yPos = baseHeight + 15 + row * legendRowHeight;
          return `
          <line x1="${xPos}" y1="${yPos - 6}" x2="${xPos + 20}" y2="${yPos - 6}" style="stroke: ${s.color}; stroke-width: 2;"/>
          <text x="${xPos + 30}" y="${yPos}" style="font-size: 12px; fill: #333;">${s.label}</text>
        `;
        })
        .join("\n")
    : "";

  // Line labels below legend (if enabled)
  const lineLabels = showLineLabels
    ? series
        .map((s, index) => {
          if (s.points.length === 0) return "";
          const row = Math.floor(index / 4);
          const col = index % 4;
          const xPos = paddingLeft + col * (chartWidth / 4) + 10;
          const yPos = baseHeight + 15 + legendRowsNeeded * legendRowHeight + row * 20;
          return `<text x="${xPos}" y="${yPos}" style="font-size: 11px; fill: ${s.color}; font-weight: 600;">${s.label}</text>`;
        })
        .join("\n")
    : "";

  return `
    <svg width="${width}" height="${height}" style="background: white; font-family: sans-serif;">
      <defs>
        <style>
          .chart-axis { stroke: #333; stroke-width: 1.5; }
          .chart-label { font-size: 12px; fill: #333; font-weight: 500; }
        </style>
      </defs>

      <!-- Background -->
      <rect width="${width}" height="${height}" fill="white"/>

      <!-- Grid lines (drawn first) -->
      ${gridLines}

      <!-- Data lines -->
      ${polylines}

      <!-- Axes (drawn last so they're on top) -->
      <line x1="${paddingLeft}" y1="${paddingTop + chartHeight}" x2="${width - paddingRight}" y2="${paddingTop + chartHeight}" class="chart-axis"/>
      <line x1="${paddingLeft}" y1="${paddingTop}" x2="${paddingLeft}" y2="${paddingTop + chartHeight}" class="chart-axis"/>

      <!-- Y-axis tick labels -->
      ${yTickLabels}

      <!-- X-axis tick labels -->
      ${xTickLabels}

      <!-- Axis titles -->
      ${yAxisTitle}
      ${xAxisTitle}

      <!-- Line labels (if enabled) -->
      ${lineLabels}

      <!-- Legend (if enabled) -->
      ${legend}
    </svg>
  `;
}

/**
 * Build a simple single-series SVG line chart for per-face temperature timeseries.
 * Uses the shared buildLineChartSvg function internally.
 * Times parameter enables real time-axis labels instead of point indices.
 */
function buildSimpleSvgLineChart(
  temps: number[],
  times: number[],
  options: { width?: number; height?: number; color?: string; label?: string } = {}
): string {
  const width = options.width ?? 700;
  const height = options.height ?? 280;
  const color = options.color ?? PDF_BRAND_BLUE;
  const label = options.label ?? "";

  // Build time-series points using real time values
  const points = downsampleTimeSeries(times, temps, Math.min(150, temps.length));

  // Convert to series format for buildLineChartSvg
  const series = [
    {
      label,
      color,
      points,
    },
  ];

  return buildLineChartSvg(series, { width, height, showLegend: label.length > 0 });
}

export function buildFaceAnalysisHtml(
  faces: FaceAnalysis[],
  times: number[],
  figureNumbers: number[],
  pageNumber: number,
  totalPages: number,
  satelliteName: string = "—",
  startTimeFormatted: string = "—",
  endTimeFormatted: string = "—"
): string {
  // Render each face's chart block stacked vertically (no stats boxes)
  const faceBlocks = faces
    .map((face, idx) => {
      const faceTemps = face.temps ?? [];
      const figureNumber = figureNumbers[idx];
      return `
        <div style="display: grid; grid-template-columns: 1fr; row-gap: 4px;">
          <!-- Temperature timeseries chart with caption -->
          ${buildSimpleSvgLineChart(faceTemps, times, { color: PDF_BRAND_BLUE })}
          <div style="font-size: 14px; color: #000; text-align: center;">Figure ${figureNumber}: ${face.displayName} Temperature vs Time</div>
        </div>
      `;
    })
    .join('<div style="margin-top: 24px;"></div>');

  // Build unified summary table for all faces using shared helper
  const faceTableRows = faces.map((face) => ({
    label: face.displayName,
    threshold: face.threshold,
    stats: face.stats,
    hasData: face.hasData,
  }));

  const title = faces.length === 2
    ? `${faces[0].displayName} & ${faces[1].displayName} Analysis`
    : `${faces[0].displayName} Analysis`;

  const contentHtml = `
    <div style="display: grid; grid-template-columns: 1fr; row-gap: 32px;">
      <!-- Charts -->
      <div style="display: grid; grid-template-columns: 1fr; row-gap: 24px;">
        ${faceBlocks}
      </div>

      <!-- Summary table -->
      <div style="display: grid; grid-template-columns: 1fr; grid-auto-rows: auto; row-gap: 8px;">
        <div style="font-size: 16px; font-weight: 600; color: #000; text-transform: uppercase;">Temperature Summary</div>
        ${buildTempTableHtml(faceTableRows, 14, 8)}
      </div>
    </div>
  `;

  return wrapPageHtml({
    title,
    pageNumber,
    totalPages,
    contentHtml,
    satelliteName,
    startTimeFormatted,
    endTimeFormatted,
  });
}

/**
 * Build subsystem analysis page(s) with 2x2 grid of mini-charts and summary table.
 * Chart-only cards (no stats box); stats appear in summary table below.
 */
export function buildSubsystemAnalysisHtml(
  subsystems: SubsystemAnalysis[],
  pageNumber: number,
  totalPages: number,
  startingFigureNumber: number,
  satelliteName: string = "—",
  startTimeFormatted: string = "—",
  endTimeFormatted: string = "—"
): string {
  // Build 2x2 grid of subsystem cards (chart-only)
  const subsystemCards = subsystems
    .map((subsystem, idx) => {
      const figureNumber = startingFigureNumber + idx;
      const chartHtml = subsystem.timeseriesChart
        ? `<img src="${subsystem.timeseriesChart}" style="max-width: 100%; height: auto;" alt="${subsystem.displayName} chart" />`
        : '<div style="background: #f5f5f5; height: 120px; display: flex; align-items: center; justify-content: center; color: #999;">No data</div>';
      return `
        <div style="display: grid; grid-template-columns: 1fr; grid-auto-rows: auto; row-gap: 4px; padding: 12px; border: 1px solid #e5e5e5; border-radius: 4px; background: white;">
          <div style="font-size: 16px; color: #000; font-weight: 600;">${subsystem.displayName}</div>
          ${chartHtml}
          <div style="font-size: 14px; color: #000; text-align: center;">Fig ${figureNumber}: ${subsystem.displayName} Temperature vs Time</div>
        </div>
      `;
    })
    .join("");

  // Build summary table using shared helper, with component name including deck
  const subsystemTableRows = subsystems.map((subsystem) => ({
    label: `${subsystem.displayName} (${subsystem.deck})`,
    threshold: subsystem.threshold,
    stats: subsystem.stats,
    hasData: subsystem.hasData,
  }));

  const contentHtml = `
    <div style="display: grid; grid-template-columns: 1fr; row-gap: 24px;">
      <!-- 2x2 Grid of subsystem mini-charts -->
      <div style="display: grid; grid-template-columns: repeat(2, 1fr); column-gap: 12px; row-gap: 12px;">
        ${subsystemCards}
      </div>

      <!-- Summary table -->
      <div style="display: grid; grid-template-columns: 1fr; grid-auto-rows: auto; row-gap: 8px;">
        <div style="font-size: 16px; font-weight: 600; color: #000; text-transform: uppercase;">Subsystem Summary</div>
        ${buildTempTableHtml(subsystemTableRows, 14, 8)}
      </div>
    </div>
  `;

  return wrapPageHtml({
    title: "Subsystem Analysis",
    pageNumber,
    totalPages,
    contentHtml,
    satelliteName,
    startTimeFormatted,
    endTimeFormatted,
  });
}

export function buildSchedulePageHtml(
  tasks: ScheduleTaskType[],
  pageNumber: number,
  totalPages: number,
  satelliteName: string = "—",
  startTimeFormatted: string = "—",
  endTimeFormatted: string = "—",
  showTitle: boolean = true
): string {
  // Helper to format epoch seconds as HH:mm:ss UTC
  const formatUtcTime = (epochSeconds: number | undefined): string => {
    if (!epochSeconds) return "--";
    const date = new Date(epochSeconds * 1000);
    const isoString = date.toISOString();
    return isoString.split("T")[1].split(".")[0];
  };

  // If no tasks, render plain text
  if (tasks.length === 0) {
    const contentHtml = `
      <div style="display: grid; grid-template-columns: 1fr; row-gap: 16px;">
        <div style="font-size: 14px; color: #666;">No scheduled tasks</div>
      </div>
    `;
    return wrapPageHtml({
      title: showTitle ? "Schedule" : "",
      pageNumber,
      totalPages,
      contentHtml,
      satelliteName,
      startTimeFormatted,
      endTimeFormatted,
    });
  }

  // Build table rows
  const tableRows = tasks
    .map((task: ScheduleTaskType) => {
      const taskNameLabel = task.taskName || "—";
      const taskSubLabel =
        task.type === "Contact Task"
          ? task.properties?.groundStationName || "—"
          : (task.properties as any)?.payloadName || "—";
      const startTimeStr = `ST: ${formatUtcTime(task.startTime)} UTC`;
      const endTimeStr = `ET: ${formatUtcTime(task.endTime)} UTC`;
      const durationMinutes = (task.endTime ?? 0) - (task.startTime ?? 0);
      const durationStr = durationMinutes > 0 ? convertDecimalMinutesToMMSS(durationMinutes / 60) : "--";
      const geoTriggerStr = task.geoTrigger || "—";
      const locationStr = task.location || "--";

      return `
        <tr style="border-bottom: 1px solid #e5e5e5;">
          <td style="padding: 8px; font-size: 14px; color: #000; text-align: left;">
            <div style="font-weight: 600;">${taskNameLabel}</div>
            <div style="font-size: 12px; color: #666;">${taskSubLabel}</div>
          </td>
          <td style="padding: 8px; font-size: 14px; color: #000; text-align: left;">
            <div>${startTimeStr}</div>
            <div>${endTimeStr}</div>
          </td>
          <td style="padding: 8px; font-size: 14px; color: #000; text-align: center;">${durationStr}</td>
          <td style="padding: 8px; font-size: 14px; color: #000; text-align: left;">
            <div>${geoTriggerStr}</div>
            <div style="font-size: 12px; color: #666;">${locationStr}</div>
          </td>
        </tr>
      `;
    })
    .join("");

  const contentHtml = `
    <div style="display: grid; grid-template-columns: 1fr; row-gap: 12px;">
      <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
        <thead>
          <tr style="background: #fafafa; border-bottom: 2px solid #e5e5e5;">
            <th style="padding: 8px; text-align: left; font-weight: 600; color: #000;">Task</th>
            <th style="padding: 8px; text-align: left; font-weight: 600; color: #000;">Trigger Time</th>
            <th style="padding: 8px; text-align: center; font-weight: 600; color: #000;">Duration</th>
            <th style="padding: 8px; text-align: left; font-weight: 600; color: #000;">Geo Trigger</th>
          </tr>
        </thead>
        <tbody>
          ${tableRows}
        </tbody>
      </table>
    </div>
  `;

  return wrapPageHtml({
    title: showTitle ? "Schedule" : "",
    pageNumber,
    totalPages,
    contentHtml,
    satelliteName,
    startTimeFormatted,
    endTimeFormatted,
  });
}

/**
 * Compute margin values and crossing status for a row (face or subsystem).
 * Margin is always shown as positive magnitude.
 * MinCrossed: simMin < thrMin. MaxCrossed: simMax > thrMax.
 */
function computeMargins(data: { threshold?: { min: number | null; max: number | null }; stats: { min: number; max: number }; hasData?: boolean }): {
  minMargin: string;
  maxMargin: string;
  minCrossed: boolean;
  maxCrossed: boolean;
} {
  if (!data.threshold || data.threshold.min === null || data.threshold.max === null || !data.hasData) {
    return { minMargin: "—", maxMargin: "—", minCrossed: false, maxCrossed: false };
  }

  const thrMin = data.threshold.min;
  const thrMax = data.threshold.max;
  const simMin = data.stats.min;
  const simMax = data.stats.max;

  const minMargin = Math.abs(simMin - thrMin);
  const maxMargin = Math.abs(simMax - thrMax);

  const minCrossed = simMin < thrMin;
  const maxCrossed = simMax > thrMax;

  return {
    minMargin: minMargin.toFixed(1),
    maxMargin: maxMargin.toFixed(1),
    minCrossed,
    maxCrossed,
  };
}

/**
 * Build a shared temperature table with two-row header: Name | Threshold (Min, Max) | Simulation (Min, Max) | Margin (Min, Max).
 * Reused for faces, subsystems (per-page), and the consolidated Simulation Temperatures page.
 */
function buildTempTableHtml(
  rows: Array<{ label: string; threshold?: { min: number | null; max: number | null }; stats: { min: number; max: number }; hasData?: boolean }>,
  fontSize: number = 13,
  paddingPx: number = 8
): string {
  const tableRows = rows
    .map((row) => {
      const margins = computeMargins(row);
      const thrMinText = row.threshold?.min !== null && row.threshold?.min !== undefined && row.hasData
        ? `${row.threshold.min.toFixed(1)}`
        : "—";
      const thrMaxText = row.threshold?.max !== null && row.threshold?.max !== undefined && row.hasData
        ? `${row.threshold.max.toFixed(1)}`
        : "—";
      const simMinText = row.hasData ? row.stats.min.toFixed(1) : "—";
      const simMaxText = row.hasData ? row.stats.max.toFixed(1) : "—";

      // Color cells based on crossing logic
      const minThresholdColor = margins.minCrossed ? "#d32f2f" : "#000";
      const minSimColor = margins.minCrossed ? "#d32f2f" : "#000";
      const minMarginColor = margins.minCrossed ? "#d32f2f" : "#4caf50";
      const minMarginWeight = margins.minCrossed ? "700" : "600";

      const maxThresholdColor = margins.maxCrossed ? "#d32f2f" : "#000";
      const maxSimColor = margins.maxCrossed ? "#d32f2f" : "#000";
      const maxMarginColor = margins.maxCrossed ? "#d32f2f" : "#4caf50";
      const maxMarginWeight = margins.maxCrossed ? "700" : "600";

      return `
        <tr style="border-bottom: 1px solid #e5e5e5; height: ${paddingPx * 4}px;">
          <td style="padding: ${paddingPx / 2}px ${paddingPx}px; font-size: ${fontSize}px; color: #000; text-align: left; font-weight: 600;">${row.label}</td>
          <!-- Threshold Min -->
          <td style="padding: ${paddingPx / 2}px ${paddingPx}px; font-size: ${fontSize}px; color: ${minThresholdColor}; text-align: center;">${thrMinText}</td>
          <!-- Threshold Max -->
          <td style="padding: ${paddingPx / 2}px ${paddingPx}px; font-size: ${fontSize}px; color: ${maxThresholdColor}; text-align: center;">${thrMaxText}</td>
          <!-- Sim Min -->
          <td style="padding: ${paddingPx / 2}px ${paddingPx}px; font-size: ${fontSize}px; color: ${minSimColor}; text-align: center;">${simMinText}</td>
          <!-- Sim Max -->
          <td style="padding: ${paddingPx / 2}px ${paddingPx}px; font-size: ${fontSize}px; color: ${maxSimColor}; text-align: center;">${simMaxText}</td>
          <!-- Margin Min -->
          <td style="padding: ${paddingPx / 2}px ${paddingPx}px; font-size: ${fontSize}px; color: ${minMarginColor}; text-align: center; font-weight: ${minMarginWeight};">${margins.minMargin}°C</td>
          <!-- Margin Max -->
          <td style="padding: ${paddingPx / 2}px ${paddingPx}px; font-size: ${fontSize}px; color: ${maxMarginColor}; text-align: center; font-weight: ${maxMarginWeight};">${margins.maxMargin}°C</td>
        </tr>
      `;
    })
    .join("");

  return `
    <table style="width: 100%; border-collapse: collapse; font-size: ${fontSize}px;">
      <thead>
        <!-- Row 1: Main headers -->
        <tr style="background: #fafafa; border-bottom: 1px solid #e5e5e5;">
          <th style="padding: ${paddingPx}px; text-align: left; font-weight: 600; color: #000; border-right: 1px solid #e5e5e5;">Component</th>
          <th colspan="2" style="padding: ${paddingPx}px; text-align: center; font-weight: 600; color: #000; border-right: 1px solid #e5e5e5;">Threshold Temps</th>
          <th colspan="2" style="padding: ${paddingPx}px; text-align: center; font-weight: 600; color: #000; border-right: 1px solid #e5e5e5;">Simulation Temps</th>
          <th colspan="2" style="padding: ${paddingPx}px; text-align: center; font-weight: 600; color: #000;">Margin</th>
        </tr>
        <!-- Row 2: Sub-headers (Min/Max) -->
        <tr style="background: #f5f5f5; border-bottom: 2px solid #e5e5e5;">
          <th style="padding: ${paddingPx / 2}px ${paddingPx}px; text-align: left; font-weight: 500; color: #333; font-size: 12px;"></th>
          <th style="padding: ${paddingPx / 2}px ${paddingPx}px; text-align: center; font-weight: 500; color: #333; font-size: 12px; border-right: 1px solid #e5e5e5;">Min</th>
          <th style="padding: ${paddingPx / 2}px ${paddingPx}px; text-align: center; font-weight: 500; color: #333; font-size: 12px; border-right: 1px solid #e5e5e5;">Max</th>
          <th style="padding: ${paddingPx / 2}px ${paddingPx}px; text-align: center; font-weight: 500; color: #333; font-size: 12px; border-right: 1px solid #e5e5e5;">Min</th>
          <th style="padding: ${paddingPx / 2}px ${paddingPx}px; text-align: center; font-weight: 500; color: #333; font-size: 12px; border-right: 1px solid #e5e5e5;">Max</th>
          <th style="padding: ${paddingPx / 2}px ${paddingPx}px; text-align: center; font-weight: 500; color: #333; font-size: 12px;">Min</th>
          <th style="padding: ${paddingPx / 2}px ${paddingPx}px; text-align: center; font-weight: 500; color: #333; font-size: 12px;">Max</th>
        </tr>
      </thead>
      <tbody>
        ${tableRows}
      </tbody>
    </table>
  `;
}

export function buildSimulationTemperaturesPageHtml(
  subsystems: SubsystemAnalysis[],
  pageNumber: number,
  totalPages: number,
  satelliteName: string = "—",
  startTimeFormatted: string = "—",
  endTimeFormatted: string = "—",
  showTitle: boolean = true
): string {
  // Build table rows using shared helper, with component name including deck
  const simTempTableRows = subsystems.map((subsystem) => ({
    label: `${subsystem.displayName} (${subsystem.deck})`,
    threshold: subsystem.threshold,
    stats: subsystem.stats,
    hasData: subsystem.hasData,
  }));

  const contentHtml = `
    <div style="display: grid; grid-template-columns: 1fr; row-gap: 12px;">
      ${buildTempTableHtml(simTempTableRows, 13, 6)}
    </div>
  `;

  return wrapPageHtml({
    title: showTitle ? "Simulation Temperatures" : "",
    pageNumber,
    totalPages,
    contentHtml,
    satelliteName,
    startTimeFormatted,
    endTimeFormatted,
  });
}

export function buildAlertsPageHtml(
  alerts: EnrichedAlert[],
  pageNumber: number,
  totalPages: number,
  satelliteName: string = "—",
  startTimeFormatted: string = "—",
  endTimeFormatted: string = "—",
  showTitle: boolean = true
): string {
  const getSeverityColor = (severity: "CRITICAL" | "WARNING") => {
    return severity === "CRITICAL"
      ? { border: "#d32f2f", bg: "#ffebee" }
      : { border: "#ff9800", bg: "#fff3e0" };
  };

  const cardRows = alerts
    .map((alert) => {
      const color = getSeverityColor(alert.severity);
      const durationMinutes = alert.durationSeconds / 60;
      const durationStr = durationMinutes > 0 ? `${durationMinutes.toFixed(1)} min` : "--";
      const subsystemsList = alert.contributingSubsystems.join(", ");

      return `
        <div style="
          border: 2px solid ${color.border};
          background-color: ${color.bg};
          border-radius: 4px;
          padding: 10px;
          font-size: 12px;
        ">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px;">
            <div style="text-align: center;">
              <div style="font-weight: 600; color: #000; font-size: 13px;">${alert.face}</div>
              <div style="color: #666; font-size: 11px;">Face</div>
            </div>
            <div style="text-align: center;">
              <div style="font-weight: 600; color: #000; font-size: 13px;">${alert.severity}</div>
              <div style="color: #666; font-size: 11px;">Severity</div>
            </div>
          </div>

          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px;">
            <div style="text-align: center;">
              <div style="font-weight: 600; color: #000;">${alert.temperature.toFixed(1)}°C</div>
              <div style="color: #666; font-size: 11px;">Peak Temperature</div>
            </div>
            <div style="text-align: center;">
              <div style="font-weight: 600; color: #000;">${alert.threshold.toFixed(1)}°C</div>
              <div style="color: #666; font-size: 11px;">Threshold</div>
            </div>
          </div>

          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px;">
            <div style="text-align: center;">
              <div style="font-weight: 600; color: #000; font-size: 11px;">ST: ${alert.startTime}</div>
              <div style="font-weight: 600; color: #000; font-size: 11px;">ET: ${alert.endTime}</div>
              <div style="color: #666; font-size: 10px;">Duration: ${durationStr}</div>
            </div>
            <div style="text-align: center;">
              <div style="font-weight: 600; color: #000; font-size: 11px;">${alert.missionPhase}</div>
              <div style="color: #666; font-size: 11px;">Mission Phase</div>
            </div>
          </div>

          <div style="margin-bottom: 8px; text-align: center;">
            <div style="font-weight: 600; color: #000; font-size: 11px;">Subsystems</div>
            <div style="color: #666; font-size: 11px; word-wrap: break-word;">${subsystemsList || "—"}</div>
          </div>

          <div style="border-top: 1px solid ${color.border}; padding-top: 8px; text-align: center;">
            <div style="color: #000; font-size: 11px; line-height: 1.4;">${alert.analysis}</div>
          </div>

          <!-- Recommendation text intentionally hidden pending Task 15 redesign -->
          <!-- <div style="margin-top: 6px; font-style: italic; color: #555; font-size: 11px;">
            ${alert.recommendation}
          </div> -->
        </div>
      `;
    })
    .join("");

  const contentHtml = `
    <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px;">
      ${cardRows}
    </div>
  `;

  return wrapPageHtml({
    title: showTitle ? "Alerts" : "",
    pageNumber,
    totalPages,
    contentHtml,
    satelliteName,
    startTimeFormatted,
    endTimeFormatted,
  });
}
